// AppRoutes.js - Extracted routing logic from App.js with lazy loading
import React, { Suspense, lazy } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { useForm } from '../contexts/FormContext';
import { PageLoading } from './LoadingStates';

// Lazy load major route components for code splitting
const BrowseTab = lazy(() => import('./BrowseTab'));
const ServerCatalogTab = lazy(() => import('./ServerCatalogTab'));
const ServerCollectionTab = lazy(() => import('./ServerCollectionTab'));
const WantlistTab = lazy(() => import('./WantlistTab'));
const CommunityTab = lazy(() => import('./CommunityTab'));
const MarketplaceTab = lazy(() => import('./MarketplaceTab'));
const AdminPortal = lazy(() => import('./AdminPortal'));
const BlogPostDetail = lazy(() => import('./BlogPostDetail'));
const ForumCategoryPage = lazy(() => import('./ForumCategoryPage'));
const ForumPostDetail = lazy(() => import('./ForumPostDetail'));
const MarketplaceItemPage = lazy(() => import('./MarketplaceItemPage'));
const TradeDetailPage = lazy(() => import('./TradeDetailPage'));
const ReleaseDetailPage = lazy(() => import('./ReleaseDetailPage'));
const UserProfile = lazy(() => import('./UserProfile'));
const UserCollectionTab = lazy(() => import('./UserCollectionTab'));
const UserWantlistTab = lazy(() => import('./UserWantlistTab'));
const About = lazy(() => import('./About'));
const PasswordResetPage = lazy(() => import('./PasswordResetPage'));
const DistributorPage = lazy(() => import('./DistributorPage'));

// Import MoviePages dynamically
const CatalogMoviePage = lazy(() =>
  import('./MoviePages').then(module => ({ default: module.CatalogMoviePage }))
);

export const AppRoutes = ({
  // User and auth
  currentUser,
  isAdmin,
  userProfile,

  // Data
  masterItems,
  allReleases,
  userCollectionItems,
  userWantlist,
  marketplaceListings,
  availableReleases,

  // VHS Data functions
  vhsData,

  // Modal functions
  modal,

  // Form functions
  form,

  // Community data
  community,

  // Search functions
  search,

  // Handler functions
  handleEditMasterItem,
  handleDeleteMasterItem,
  handleNewForumPost,
  handleNewTradeRequest,
  handleJoinChallenge,
  handleWantlistModalClick,
  handleCollectionModalClick,

  // Marketplace functions
  addMarketplaceListing,
  removeMarketplaceListing,
  isItemListed,
  getSellerRating,

  // Wantlist functions
  addReleaseToWantlist,
  removeReleaseFromWantlist,
  isReleaseWanted,
  hasWantedReleases,
  addMovieToWantlist,
  removeMovieFromWantlist,
  isMovieWanted,

  // Other props
  supabase,
  supabaseUrl,
  supabaseAnonKey,
  setUploadingImage,
  setUnreadMarketplaceCount,
  setUnreadCommunityCount,
  refreshUnreadCommunityCount,
  unreadCommunityCount,
}) => {
  // Removed unused state: viewMode, setViewMode
  const navigate = useNavigate();
  const formContext = useForm();

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Home route */}
        <Route
          path="/"
          element={
            <BrowseTab
              masterItems={masterItems}
              allReleases={allReleases}
              currentUser={currentUser}
              userCollectionItems={userCollectionItems}
              communityStats={community.communityStats}
              marketplaceListings={marketplaceListings}
              activityFeed={community.activityFeed}
              onSearchQueryChange={search.setSearchQuery}
              onEditItem={item => {
                const editItem = handleEditMasterItem(item);
                form.setEditingMasterItem(editItem);
                modal.openAddMasterModal(form.editingMasterItem, form.isDataFromSearch);
              }}
              onDeleteItem={handleDeleteMasterItem}
              onAddToCollection={item => {
                form.setSelectedMasterItem(item);
                modal.openAddToCollectionModal(form.selectedMasterItem);
              }}
              onPosterClick={(posterUrl, title) => modal.openImageModal(posterUrl, '')}
            />
          }
        />

        {/* Catalog routes - Using ServerCatalogTab with server-side pagination */}
        <Route
          path="/catalog"
          element={
            <ServerCatalogTab
              supabase={supabase}
              allReleases={allReleases}
              currentUser={currentUser}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              isAdmin={isAdmin}
              hasWantedReleases={hasWantedReleases}
              onWantlistClick={handleWantlistModalClick}
              onCollectionClick={handleCollectionModalClick}
              onEditItem={item => {
                const editItem = handleEditMasterItem(item);
                form.setEditingMasterItem(editItem);
                modal.openAddMasterModal(form.editingMasterItem, form.isDataFromSearch);
              }}
              onDeleteItem={handleDeleteMasterItem}
              onAddToCollection={item => {
                form.setSelectedMasterItem(item);
                modal.openAddToCollectionModal(form.selectedMasterItem);
              }}
              onPosterClick={(posterUrl, title) => modal.openImageModal(posterUrl, '')}
            />
          }
        />

        {/* Individual catalog movie route */}
        <Route
          path="/catalog/:slug"
          element={
            <CatalogMoviePage
              masterItems={masterItems}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              marketplaceListings={marketplaceListings}
              tradeRequests={community.tradeRequests}
              currentUser={currentUser}
              isAdmin={isAdmin}
              userProfile={userProfile}
              supabase={supabase}
              loadMovieReleases={vhsData.loadMovieReleases}
              addToCollectionWithRelease={vhsData.addToCollectionWithRelease}
              loadUserCollection={vhsData.loadUserCollection}
              loadReleaseImages={vhsData.loadReleaseImages}
              addReleaseToWantlist={addReleaseToWantlist}
              removeReleaseFromWantlist={removeReleaseFromWantlist}
              isReleaseWanted={isReleaseWanted}
              addMovieToWantlist={addMovieToWantlist}
              removeMovieFromWantlist={removeMovieFromWantlist}
              isMovieWanted={isMovieWanted}
              removeFromCollection={vhsData.removeFromCollection}
              addMarketplaceListing={addMarketplaceListing}
              updateCollectionNotes={vhsData.updateCollectionNotes}
              activityFeed={community.activityFeed}
              onAddTradeRequest={handleNewTradeRequest}
            />
          }
        />

        {/* Individual release pages - new format */}
        <Route
          path="/catalog/release/:slug"
          element={
            <ReleaseDetailPage
              masterItems={masterItems}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              marketplaceListings={marketplaceListings}
              currentUser={currentUser}
              isAdmin={isAdmin}
              userProfile={userProfile}
              supabase={supabase}
              addToCollectionWithRelease={vhsData.addToCollectionWithRelease}
              loadUserCollection={vhsData.loadUserCollection}
              addReleaseToWantlist={addReleaseToWantlist}
              removeReleaseFromWantlist={removeReleaseFromWantlist}
              isReleaseWanted={isReleaseWanted}
              addMarketplaceListing={addMarketplaceListing}
              removeMarketplaceListing={removeMarketplaceListing}
              isItemListed={isItemListed}
              updateCollectionNotes={vhsData.updateCollectionNotes}
              activityFeed={community.activityFeed}
              tradeRequests={community.tradeRequests}
              onAddTradeRequest={handleNewTradeRequest}
            />
          }
        />

        {/* Individual release pages - legacy format */}
        <Route
          path="/catalog/:slug/release/:releaseId"
          element={
            <ReleaseDetailPage
              masterItems={masterItems}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              marketplaceListings={marketplaceListings}
              currentUser={currentUser}
              isAdmin={isAdmin}
              userProfile={userProfile}
              supabase={supabase}
              addToCollectionWithRelease={vhsData.addToCollectionWithRelease}
              loadUserCollection={vhsData.loadUserCollection}
              addReleaseToWantlist={addReleaseToWantlist}
              removeReleaseFromWantlist={removeReleaseFromWantlist}
              isReleaseWanted={isReleaseWanted}
              addMarketplaceListing={addMarketplaceListing}
              removeMarketplaceListing={removeMarketplaceListing}
              isItemListed={isItemListed}
              updateCollectionNotes={vhsData.updateCollectionNotes}
              activityFeed={community.activityFeed}
              tradeRequests={community.tradeRequests}
              onAddTradeRequest={handleNewTradeRequest}
            />
          }
        />

        {/* Collection route - Using ServerCollectionTab with server-side pagination */}
        <Route
          path="/collection"
          element={
            <ServerCollectionTab
              supabase={supabase}
              currentUser={currentUser}
              isAdmin={isAdmin}
              userCollectionItems={userCollectionItems}
              updateCollectionNotes={vhsData.updateCollectionNotes}
              onRemoveFromCollection={vhsData.removeFromCollection}
              onAddMarketplaceListing={addMarketplaceListing}
              onRemoveMarketplaceListing={removeMarketplaceListing}
              removeFromCollectionAndMarketplace={vhsData.removeFromCollectionAndMarketplace}
              isItemListed={isItemListed}
              userProfile={userProfile}
              onPosterClick={(imageUrl, title) => {
                modal.openModal('imageModal', { imageUrl, title: '' });
              }}
              onAddTradeRequest={handleNewTradeRequest}
              addActivityFeedItem={item => {
                community.setActivityFeed([item, ...community.activityFeed]);
              }}
            />
          }
        />

        {/* Wantlist route */}
        <Route
          path="/wantlist"
          element={
            <WantlistTab
              userWantlist={userWantlist}
              masterItems={masterItems}
              currentUser={currentUser}
              marketplaceListings={marketplaceListings}
              tradeRequests={community.tradeRequests}
              removeFromWantlist={removeReleaseFromWantlist}
              removeMovieFromWantlist={removeMovieFromWantlist}
              addMarketplaceListing={addMarketplaceListing}
              removeMarketplaceListing={removeMarketplaceListing}
              isItemListed={isItemListed}
              updateCollectionNotes={vhsData.updateCollectionNotes}
              activityFeed={community.activityFeed}
              addToCollectionWithRelease={vhsData.addToCollectionWithRelease}
              loadUserCollection={vhsData.loadUserCollection}
            />
          }
        />

        {/* Community route */}
        <Route
          path="/community"
          element={
            <CommunityTab
              communitySubTab={community.communitySubTab}
              setCommunitySubTab={community.setCommunitySubTab}
              communityStats={community.communityStats}
              activityFeed={community.activityFeed}
              setActivityFeed={community.setActivityFeed}
              currentUser={currentUser}
              userProfile={userProfile}
              selectedForumCategory={form.selectedForumCategory}
              setSelectedForumCategory={form.setSelectedForumCategory}
              newPostContent={form.postForm.content}
              setNewPostContent={form.setPostContent}
              newPostTitle={form.postForm.title}
              setNewPostTitle={form.setPostTitle}
              filterCategory={search.filterCategory}
              setFilterCategory={search.setFilterCategory}
              handleNewForumPost={handleNewForumPost}
              forumPosts={community.forumPosts}
              tradeRequests={community.tradeRequests}
              handleNewTradeRequest={handleNewTradeRequest}
              challenges={community.challenges}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              supabase={supabase}
              isAdmin={isAdmin}
              onUnreadCountChange={setUnreadCommunityCount}
              refreshUnreadCount={refreshUnreadCommunityCount}
              unreadCount={unreadCommunityCount}
            />
          }
        />

        {/* Forum Category route */}
        <Route
          path="/community/forums/category/:category"
          element={
            <ForumCategoryPage
              supabase={supabase}
              currentUser={currentUser}
              userProfile={userProfile}
              forumPosts={community.forumPosts}
              newPostTitle={form.postForm.title}
              setNewPostTitle={form.setPostTitle}
              newPostContent={form.postForm.content}
              setNewPostContent={form.setPostContent}
              handleNewForumPost={handleNewForumPost}
              selectedForumCategory={form.selectedForumCategory}
              setSelectedForumCategory={form.setSelectedForumCategory}
            />
          }
        />

        {/* Forum Post Detail route */}
        <Route
          path="/community/forums/:postId"
          element={
            <ForumPostDetail
              supabase={supabase}
              currentUser={currentUser}
              userProfile={userProfile}
              forumPosts={community.forumPosts}
            />
          }
        />

        {/* Marketplace route */}
        <Route
          path="/marketplace"
          element={
            <MarketplaceTab
              marketplaceListings={marketplaceListings}
              currentUser={currentUser}
              userProfile={userProfile}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              removeMarketplaceListing={removeMarketplaceListing}
              setActiveTab={() => {}}
              supabase={supabase}
              isAdmin={isAdmin}
              onUnreadCountChange={setUnreadMarketplaceCount}
              addReleaseToWantlist={addReleaseToWantlist}
              addMovieToWantlist={addMovieToWantlist}
              removeReleaseFromWantlist={removeReleaseFromWantlist}
              removeMovieFromWantlist={removeMovieFromWantlist}
            />
          }
        />

        {/* Individual trade listing route */}
        <Route
          path="/community/trade/:id"
          element={
            <TradeDetailPage
              currentUser={currentUser}
              userProfile={userProfile}
              supabase={supabase}
            />
          }
        />

        {/* Individual marketplace item route */}
        <Route
          path="/marketplace/item/:id"
          element={
            <MarketplaceItemPage
              marketplaceListings={marketplaceListings}
              currentUser={currentUser}
              supabase={supabase}
              getSellerRating={getSellerRating}
            />
          }
        />

        {/* Admin Portal route */}
        {isAdmin && (
          <Route
            path="/admin"
            element={
              <AdminPortal
                currentUser={currentUser}
                supabase={supabase}
                isAdmin={isAdmin}
                supabaseUrl={supabaseUrl}
                supabaseAnonKey={supabaseAnonKey}
                vhsData={vhsData}
                userProfile={userProfile}
                activityFeed={community.activityFeed}
                setActivityFeed={community.setActivityFeed}
                setUploadingImage={setUploadingImage}
                setShowAddMasterForm={show =>
                  show ? modal.openModal('addMaster') : modal.closeModal('addMaster')
                }
                setShowMovieSearch={show =>
                  show ? modal.openModal('movieSearch') : modal.closeModal('movieSearch')
                }
                showAddMasterForm={modal.isModalOpen('addMaster')}
                showMovieSearch={modal.isModalOpen('movieSearch')}
                setBulkImportMovieTitle={formContext.setBulkImportMovieTitle}
                loadUserCollection={vhsData.loadUserCollection}
              />
            }
          />
        )}

        {/* Blog Post Detail route */}
        <Route
          path="/blog/:slug"
          element={
            <BlogPostDetail supabase={supabase} currentUser={currentUser} isAdmin={isAdmin} />
          }
        />

        {/* User Profile route */}
        <Route path="/users/:username" element={<UserProfile supabase={supabase} />} />

        {/* User Collection route */}
        <Route
          path="/users/:username/collection"
          element={
            <UserCollectionTab
              supabase={supabase}
              currentUser={currentUser}
              onPosterClick={(imageUrl, title) =>
                modal.openModal('imageModal', { imageUrl, title: '' })
              }
            />
          }
        />

        {/* User Wantlist route */}
        <Route
          path="/users/:username/wantlist"
          element={
            <UserWantlistTab
              supabase={supabase}
              currentUser={currentUser}
              onPosterClick={(imageUrl, title) =>
                modal.openModal('imageModal', { imageUrl, title: '' })
              }
            />
          }
        />

        {/* Distributor route */}
        <Route
          path="/distributors/:distributorName"
          element={
            <DistributorPage
              supabase={supabase}
              currentUser={currentUser}
              userCollectionItems={userCollectionItems}
              userWantlist={userWantlist}
              isAdmin={isAdmin}
              addReleaseToWantlist={addReleaseToWantlist}
              removeReleaseFromWantlist={removeReleaseFromWantlist}
              isReleaseWanted={isReleaseWanted}
              addMovieToWantlist={addMovieToWantlist}
              removeMovieFromWantlist={removeMovieFromWantlist}
              isMovieWanted={isMovieWanted}
              onPosterClick={(imageUrl, title) => modal.openImageModal(imageUrl, title)}
            />
          }
        />

        {/* Password Reset route */}
        <Route path="/auth/reset-password" element={<PasswordResetPage />} />

        {/* About route */}
        <Route path="/about" element={<About />} />

        {/* Catch-all route for invalid URLs */}
        <Route
          path="*"
          element={
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
              <p className="text-gray-600 mb-6">
                The page you're looking for doesn't exist or may have been moved.
              </p>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Go Home
              </button>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
};
